import React from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage, injectIntl } from 'react-intl'
import styled from 'styled-components'
import { Tabs, Row, Col, Skeleton } from 'core/components/ui'
import SVG from 'core/components/svg'
import svg from 'assets/svg'
import { font, greys } from 'assets/themes/theme-default'
import { stockShape } from '../../shapes'

const { TabPane } = Tabs

const TabBarIcon = styled(SVG)`
  width: 36px;
  height: 36px;
  fill: ${props => props.theme.colors.color2.bg};
  transition: all 200ms;
`
const StyledTabs = styled(Tabs)`
  &&& {
    line-height: 19px;
    color: ${props => props.theme.colors.color2.bg};
    ${TabBarIcon} {
      fill: ${props => props.theme.colors.color2.bg};
      vertical-align: bottom;
    }
    .ant-tabs-bar {
      border-color: ${greys.light3};
      display: flex;
      align-items: center;
      span {
        display: none;
        font-size: ${font.size.lg};
        text-transform: uppercase;
        font-weight: ${font.weight.bold};
        color: ${props => props.theme.colors.color1.bg};
      }
    }
    .ant-tabs-ink-bar {
      background-color: ${props => props.theme.colors.color1.bg};
      height: 3px;
    }
    .ant-tabs-tab-active {
      span {
        display: inline-block;
      }

      ${TabBarIcon} {
        margin: 0 15px 0 0;
        fill: ${props => props.theme.colors.color1.bg};
      }
    }
  }
`
const TabPaneTitle = styled.h5`
  color: ${props => props.theme.colors.color2.bg};
`
const TabPaneRow = styled(Row)`
  margin: 10px 0;
  font-size: ${font.size.sm};
`
const TabPaneColValue = styled(Row)`
  font-weight: ${font.weight.bold};
`
const RenderTabPaneColOptions = styled(Col)`
  margin-bottom: 5px;
  > div {
    padding-bottom: 5px;
    border-bottom: 1px solid ${props => props.theme.colors.color1.bg};
  }
`

const renderTabPaneTitle = title => <TabPaneTitle>{title}</TabPaneTitle>
const renderTabPaneRowDatas = (key, value) => (
  <TabPaneRow type="flex">
    <Col span={14}>{key}</Col>
    <TabPaneColValue span={10}>{value}</TabPaneColValue>
  </TabPaneRow>
)

const TabsInfos = props => {
  const { stock, intl, loading } = props

  const { selectedVehicle } = stock

  const {
    CV,
    CH,
    typeOfEngine,
    cylinder,
    transmission,
    maxSpeed,
    acceleration,
    options,
    consumption,
    seating,
    dimensions,
    weight,
    tires,
    trunk,
    load
  } = selectedVehicle

  const renderPane1 = loading ? (
    <Skeleton active />
  ) : (
    <Row type="flex" gutter={15}>
      <Col span={12}>
        {renderTabPaneTitle(<FormattedMessage id="stock-vehicle-detail-tabs-tabpane-1-title-1" />)}

        {typeOfEngine &&
          renderTabPaneRowDatas(
            <FormattedMessage id="stock-vehicle-detail-tabs-tabpane-1-key-1" />,
            typeOfEngine
          )}

        {CV &&
          CH &&
          renderTabPaneRowDatas(
            <FormattedMessage id="stock-vehicle-detail-tabs-tabpane-1-key-2" />,
            `${intl.formatNumber(CV)} kW / ${intl.formatNumber(CH)} ch DIN`
          )}

        {cylinder &&
          renderTabPaneRowDatas(
            <FormattedMessage id="stock-vehicle-detail-tabs-tabpane-1-key-3" />,
            `${intl.formatNumber(cylinder)} cc`
          )}
        {transmission &&
          renderTabPaneRowDatas(
            <FormattedMessage id="stock-vehicle-detail-tabs-tabpane-1-key-4" />,
            transmission
          )}
      </Col>
      <Col span={12}>
        {renderTabPaneTitle(<FormattedMessage id="stock-vehicle-detail-tabs-tabpane-1-title-2" />)}
        {maxSpeed &&
          renderTabPaneRowDatas(
            <FormattedMessage id="stock-vehicle-detail-tabs-tabpane-1-key-5" />,
            maxSpeed
          )}
        {acceleration &&
          renderTabPaneRowDatas(
            <FormattedMessage id="stock-vehicle-detail-tabs-tabpane-1-key-6" />,
            `${intl.formatNumber(acceleration)} s`
          )}
      </Col>
    </Row>
  )

  const renderPane2 = loading ? (
    <Skeleton active />
  ) : (
    <Row type="flex" gutter={15}>
      <Col span={12}>
        {consumption &&
          consumption.urban &&
          renderTabPaneRowDatas(
            <FormattedMessage id="stock-vehicle-detail-tabs-tabpane-2-key-1" />,
            `${intl.formatNumber(consumption.urban)} l/km`
          )}

        {consumption &&
          consumption.extraUrban &&
          renderTabPaneRowDatas(
            <FormattedMessage id="stock-vehicle-detail-tabs-tabpane-2-key-2" />,
            `${intl.formatNumber(consumption.extraUrban)} l/km`
          )}

        {consumption &&
          consumption.mixed &&
          renderTabPaneRowDatas(
            <FormattedMessage id="stock-vehicle-detail-tabs-tabpane-2-key-3" />,
            `${intl.formatNumber(consumption.mixed)} l/km`
          )}
      </Col>
    </Row>
  )
  const renderPane3 = loading ? (
    <Skeleton active />
  ) : (
    <TabPaneRow type="flex" gutter={30}>
      {options &&
        options.map(item => (
          <RenderTabPaneColOptions span={12} key={item}>
            <div>{item}</div>
          </RenderTabPaneColOptions>
        ))}
    </TabPaneRow>
  )

  const renderPane4 = loading ? (
    <Skeleton active />
  ) : (
    <Row type="flex" gutter={15}>
      <Col span={12}>
        {seating &&
          renderTabPaneRowDatas(
            <FormattedMessage id="stock-vehicle-detail-tabs-tabpane-4-key-1" />,
            seating
          )}
        {dimensions &&
          dimensions.length &&
          renderTabPaneRowDatas(
            <FormattedMessage id="stock-vehicle-detail-tabs-tabpane-4-key-2" />,
            `${intl.formatNumber(dimensions.length)} mm`
          )}
        {dimensions &&
          dimensions.width &&
          renderTabPaneRowDatas(
            <FormattedMessage id="stock-vehicle-detail-tabs-tabpane-4-key-3" />,
            `${intl.formatNumber(dimensions.width)} mm`
          )}
        {dimensions &&
          dimensions.height &&
          renderTabPaneRowDatas(
            <FormattedMessage id="stock-vehicle-detail-tabs-tabpane-4-key-4" />,
            `${intl.formatNumber(dimensions.height)} mm`
          )}
        {tires &&
          tires.front &&
          renderTabPaneRowDatas(
            <FormattedMessage id="stock-vehicle-detail-tabs-tabpane-4-key-5" />,
            tires.front
          )}
        {tires &&
          tires.back &&
          renderTabPaneRowDatas(
            <FormattedMessage id="stock-vehicle-detail-tabs-tabpane-4-key-6" />,
            tires.back
          )}
      </Col>
      <Col span={12}>
        {weight &&
          weight.total &&
          renderTabPaneRowDatas(
            <FormattedMessage id="stock-vehicle-detail-tabs-tabpane-4-key-7" />,
            `${intl.formatNumber(weight.total)} kg`
          )}
        {weight &&
          weight.unloaded &&
          renderTabPaneRowDatas(
            <FormattedMessage id="stock-vehicle-detail-tabs-tabpane-4-key-8" />,
            `${intl.formatNumber(weight.unloaded)} kg`
          )}
        {trunk &&
          trunk.min &&
          renderTabPaneRowDatas(
            <FormattedMessage id="stock-vehicle-detail-tabs-tabpane-4-key-9" />,
            `${intl.formatNumber(trunk.min)} l`
          )}
        {trunk &&
          trunk.max &&
          renderTabPaneRowDatas(
            <FormattedMessage id="stock-vehicle-detail-tabs-tabpane-4-key-10" />,
            `${intl.formatNumber(trunk.max)} l`
          )}
        {load &&
          load.payload &&
          renderTabPaneRowDatas(
            <FormattedMessage id="stock-vehicle-detail-tabs-tabpane-4-key-11" />,
            `${intl.formatNumber(load.payload)} kg`
          )}
        {load &&
          load.roof &&
          renderTabPaneRowDatas(
            <FormattedMessage id="stock-vehicle-detail-tabs-tabpane-4-key-12" />,
            `${intl.formatNumber(load.roof)} kg`
          )}
      </Col>
    </Row>
  )

  return (
    <StyledTabs defaultActiveKey="1" animated={false}>
      <TabPane
        tab={
          <>
            <TabBarIcon svg={svg.power} />
            <FormattedMessage id="stock-vehicle-detail-tabs-tabbar-title-1" />
          </>
        }
        key="1"
      >
        {renderPane1}
      </TabPane>
      <TabPane
        tab={
          <>
            <TabBarIcon svg={svg.consumption} />
            <FormattedMessage id="stock-vehicle-detail-tabs-tabbar-title-2" />
          </>
        }
        key="2"
      >
        {renderPane2}
      </TabPane>
      <TabPane
        tab={
          <>
            <TabBarIcon svg={svg.wrench} />
            <FormattedMessage id="stock-vehicle-detail-tabs-tabbar-title-3" />
          </>
        }
        key="3"
      >
        {renderPane3}
      </TabPane>
      <TabPane
        tab={
          <>
            <TabBarIcon svg={svg.dimension} />
            <FormattedMessage id="stock-vehicle-detail-tabs-tabbar-title-4" />
          </>
        }
        key="4"
      >
        {renderPane4}
      </TabPane>
    </StyledTabs>
  )
}

TabsInfos.propTypes = {
  intl: PropTypes.shape().isRequired,
  stock: stockShape.isRequired,
  loading: PropTypes.bool.isRequired
}

export default injectIntl(TabsInfos)
